import { useState, useEffect } from 'react';
import { useAuth } from '@/providers/AuthProvider';
import { apiClient } from '@/lib/api-client';
import { useWhiteLabelAccess } from '@/services/subscription';

interface WhiteLabelSettings {
  id: string;
  organizationId: string;
  logoUrl?: string;
  faviconUrl?: string;
  primaryColor: string;
  secondaryColor: string;
  customDomain?: string;
  emailSenderName?: string;
  emailSenderEmail?: string;
  removeBranding: boolean;
  domainVerified: boolean;
  createdAt: string;
  updatedAt: string;
}

interface UseWhiteLabelReturn {
  settings: WhiteLabelSettings | null;
  loading: boolean;
  error: Error | null;
  saving: boolean;
  saveError: Error | null;
  hasAccess: boolean;
  updateSettings: (updates: Partial<WhiteLabelSettings>) => Promise<void>;
  resetSettings: () => Promise<void>;
  refreshSettings: () => Promise<void>;
}

export function useWhiteLabel(): UseWhiteLabelReturn {
  const { organization, token } = useAuth();
  const [settings, setSettings] = useState<WhiteLabelSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  // Kept separate from `loading`/`error` (the initial-fetch state) — a failed
  // save should show an inline error on the form, not replace the whole page
  // with the "failed to load settings" screen.
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<Error | null>(null);

  // Use the proper subscription access check
  const { data: accessData, isLoading: accessLoading } = useWhiteLabelAccess();
  const hasAccess = accessData?.hasAccess || false;

  // Load white label settings
  const loadSettings = async () => {
    if (!organization?.id || !hasAccess) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const data = await apiClient.get<WhiteLabelSettings>(
        '/api/v1/white-label/settings',
        token
      );
      setSettings(data);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error');
      setError(error);
      console.error('Failed to load white label settings:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load settings when access is determined
  useEffect(() => {
    if (!accessLoading) {
      if (hasAccess) {
        loadSettings();
      } else {
        setLoading(false);
      }
    }
  }, [hasAccess, organization?.id, accessLoading]);

  // Update settings
  const updateSettings = async (updates: Partial<WhiteLabelSettings>) => {
    if (!organization?.id) return;

    try {
      setSaving(true);
      setSaveError(null);

      const updatedSettings = await apiClient.request<WhiteLabelSettings>(
        '/api/v1/white-label/settings',
        { method: 'PUT', body: updates, authToken: token }
      );
      setSettings(updatedSettings);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error');
      setSaveError(error);
      throw error;
    } finally {
      setSaving(false);
    }
  };

  // Reset settings
  const resetSettings = async () => {
    if (!organization?.id) return;

    try {
      setSaving(true);
      setSaveError(null);

      await apiClient.request<void>(
        '/api/v1/white-label/reset',
        { method: 'DELETE', authToken: token }
      );

      // Reload settings after reset
      await loadSettings();
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error');
      setSaveError(error);
      throw error;
    } finally {
      setSaving(false);
    }
  };

  // Refresh settings
  const refreshSettings = async () => {
    await loadSettings();
  };

  return {
    settings,
    loading,
    error,
    saving,
    saveError,
    hasAccess,
    updateSettings,
    resetSettings,
    refreshSettings,
  };
}
